import { cn } from "@/lib/utils";
import { stripAudioTags } from "@/features/utils/audio-tags";
import {
  Play,
  Pause,
  Microphone,
  Headset,
  ShieldCheck,
} from "@phosphor-icons/react";
import React from "react";
import { type MessageMetrics, type SessionMessage } from "../../types";
import { Tooltip } from "@/components/ui/tooltip";
import { CalendarEventAttachment } from "../attachments";
import { type BookingEvent } from "../../hooks/use-public-booking";
import { AgentAvatar } from "@/components/molecules/agent-avatar";
import { useDateFormat, usePublicTranslation } from "@/features/utils/i18n";
import { Bubble, BubbleRow } from "./Bubble";

interface ChatMessageProps {
  message: SessionMessage;
  onPlayMessage?: (timestamp: number) => void;
  playingMessageTimestamp?: number | null;
  events?: BookingEvent[];
  onEventClick?: (event: BookingEvent) => void;
  isDebugMode?: boolean;
  /** When set, agent messages are prefixed with the agent avatar (public chat only). */
  showAgentAvatar?: boolean;
  agentName?: string;
  agentLogo?: string | null;
  /**
   * `compact` is the back-office review: the team reads a whole call at once, so the
   * bubbles lose their padding and the time moves inline. The visitor keeps `chat`.
   */
  density?: "chat" | "compact";
}

// Parse markdown-like syntax for bold and italics
export function parseFormattedText(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let currentText = "";
  let i = 0;
  let key = 0;

  const flushText = () => {
    if (currentText) {
      parts.push(currentText);
      currentText = "";
    }
  };

  while (i < text.length) {
    // Check for formatting markers in order of priority: **, __, *, _
    let marker = "";
    let markerLength = 0;
    let isBold = false;

    if (text.slice(i, i + 2) === "**") {
      marker = "**";
      markerLength = 2;
      isBold = true;
    } else if (text.slice(i, i + 2) === "__") {
      marker = "__";
      markerLength = 2;
      isBold = false;
    } else if (text[i] === "*" && text[i + 1] !== "*") {
      marker = "*";
      markerLength = 1;
      isBold = true;
    } else if (text[i] === "_" && text[i + 1] !== "_") {
      marker = "_";
      markerLength = 1;
      isBold = false;
    }

    if (marker) {
      // Find the closing marker
      const endIndex = text.indexOf(marker, i + markerLength);
      if (endIndex !== -1) {
        // Flush any pending text
        flushText();
        // Add the formatted text
        const formattedText = text.slice(i + markerLength, endIndex);
        const element = isBold ? (
          <span key={key++} className="font-semibold">
            {formattedText}
          </span>
        ) : (
          <em key={key++}>{formattedText}</em>
        );
        parts.push(element);
        i = endIndex + markerLength;
        continue;
      }
    }

    // No marker found, add character to current text
    currentText += text[i];
    i++;
  }

  // Flush any remaining text
  flushText();

  return parts;
}

export function ChatMessage({
  message,
  onPlayMessage,
  playingMessageTimestamp,
  events,
  onEventClick,
  isDebugMode,
  showAgentAvatar,
  agentName,
  agentLogo,
  density = "chat",
}: ChatMessageProps) {
  const { t } = usePublicTranslation();
  const { format } = useDateFormat();

  // Tool messages are now handled separately in ChatMessages
  if (message.type === "tool") {
    return null;
  }

  // An administrator's instruction to the agent: neither side of the conversation, so
  // it sits in the middle of the thread, like the tools — and says who cannot read it.
  if (message.type === "event" && message.event === "admin_message") {
    return (
      <div className="flex justify-center py-1">
        <div className="max-w-[85%] rounded-xl border border-dashed border-violet-400/60 bg-violet-50 px-3 py-2 dark:border-violet-400/40 dark:bg-violet-500/10">
          <div className="mb-0.5 flex items-center gap-1.5 text-xs font-medium text-violet-700 dark:text-violet-300">
            <ShieldCheck size={12} weight="fill" />
            {t("chat.admin_message")}
            <span className="font-normal opacity-70">
              · {t("chat.admin_message_hidden")}
            </span>
            <span className="ml-auto pl-3 font-normal opacity-70">
              {format(message.timestamp, "p")}
            </span>
          </div>
          <p className="whitespace-pre-wrap text-sm leading-snug">
            {(message.value || "").trim()}
          </p>
        </div>
      </div>
    );
  }

  const handlePlayAudio = () => {
    onPlayMessage?.(message.timestamp);
  };

  // An expressive voice's audio tags ("[laughs]") are performed, not said: the stored
  // message keeps them, the reader does not see them.
  const text =
    message.type === "agent"
      ? stripAudioTags(message.value || "").trim()
      : (message.value || "").trim();
  // A reply that was nothing but a tag has nothing left to show.
  if (message.type === "agent" && !text && !message.attachments?.length) {
    return null;
  }

  const isPlaying = playingMessageTimestamp === message.timestamp;
  const isUser = message.type === "user";
  // A team member: on our side of the thread, but never under the agent's avatar —
  // the whole point is to tell the person who answered from the AI.
  const isHuman = message.type === "human";
  const memberName = (message.metadata?.member as { name?: string } | undefined)
    ?.name;
  const withAvatar = showAgentAvatar && !isUser && !isHuman;

  // A user turn that reached us as a speech-to-text transcript (the user spoke over the
  // audio bridge instead of typing). Flag it visually so a session mixing typed and spoken
  // turns stays legible: italic text + a microphone glyph.
  const isTranscribed =
    (message.type === "user" || isHuman) && message.via === "voice";
  const compact = density === "compact";

  // In the language the conversation is read in — the visitor's on the widget, the
  // team's in the back-office — rather than a fixed `fr-FR` an English visitor would
  // read as 24h French. The tooltip is the full date, for the reader who asks: the
  // raw ISO string it used to show was a UTC timestamp nobody could read at a glance.
  const time = (
    <Tooltip content={format(message.timestamp, "PPPP, pp")}>
      <span className={cn("opacity-70", compact ? "text-[11px]" : "text-xs")}>
        {format(message.timestamp, "p")}
      </span>
    </Tooltip>
  );
  const playButton = onPlayMessage && (
    <button
      onClick={handlePlayAudio}
      className="text-xs opacity-70 hover:opacity-100 transition-opacity flex items-center gap-1"
      title={isPlaying ? t("chat.pause") : t("chat.play")}
    >
      {isPlaying ? (
        <Pause size={compact ? 12 : 14} weight="fill" />
      ) : (
        <Play size={compact ? 12 : 14} weight="fill" />
      )}
    </button>
  );

  return (
    <BubbleRow
      side={isUser ? "user" : "agent"}
      // Only user bubbles fade in on mount. Agent bubbles are the commit target
      // of the streaming bubble — fading them in would replay a fade (white
      // flash) the moment the live stream is swapped for the final message.
      className={cn(isUser && "animate-fade-in")}
      avatar={
        withAvatar && (
          <AgentAvatar
            name={agentName}
            logo={agentLogo}
            className="mb-1 size-7"
          />
        )
      }
    >
      <Bubble
        side={isUser ? "user" : "agent"}
        density={density}
        className={cn("overflow-hidden", isHuman && "ring-1 ring-amber-500/40")}
      >
        {isHuman && (
          <div className="mb-1 flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-400">
            <Headset size={12} weight="fill" />
            {memberName || t("chat.team_member")}
          </div>
        )}
        <p
          className={cn(
            "text-sm whitespace-pre-wrap",
            compact ? "leading-snug" : "leading-relaxed",
            isTranscribed && "italic",
          )}
        >
          {/* Compact: the time floats at the end of the text, on its last line when
              there is room, instead of costing a line of its own. */}
          {compact && (
            <span className="float-right ml-3 mt-0.5 flex items-center gap-1.5 not-italic">
              {playButton}
              {time}
            </span>
          )}
          {/* Filler hooks played during this agent turn, shown greyed at the very start of
              the phrase (handy to debug awkward pauses). */}
          {message.type === "agent" && !!message.metadata?.hooks?.length && (
            <span className="opacity-50" title="Hook joué pendant ce tour">
              {message.metadata.hooks.join(" ")}{" "}
            </span>
          )}
          {/* Transcribed user turn: a microphone glyph makes the STT origin obvious. */}
          {isTranscribed && (
            <Microphone
              size={14}
              weight="fill"
              className="inline-block mr-1 -mt-0.5 align-middle opacity-70"
              aria-label={t("chat.transcribed")}
            />
          )}
          {/* Trimmed here too: the backend now stores messages trimmed, but a bubble
              rendered with `whitespace-pre-wrap` would still show the blank first/last
              line of everything persisted before that. */}
          {parseFormattedText(text)}
        </p>

        {/* Render attachments */}
        {message.attachments && message.attachments.length > 0 && (
          <div className="space-y-2">
            {message.attachments.map((attachment, idx) => {
              if (attachment.type === "calendar_event" && onEventClick) {
                return (
                  <CalendarEventAttachment
                    key={`${attachment.id}-${idx}`}
                    eventId={attachment.id}
                    events={events}
                    onEventClick={onEventClick}
                  />
                );
              }
              return null;
            })}
          </div>
        )}

        {!compact && (
          <div className="flex items-center justify-between mt-1 gap-2">
            {time}
            {playButton}
          </div>
        )}

        {/* Sysadmin/debug: per-turn technical metrics — shows the starter (Bedrock) TTFT
            next to the main model so its latency benefit is visible per message. */}
        {isDebugMode && message.type === "agent" && (
          <MessageMetricsDebug metrics={message.metadata?.metrics} />
        )}
      </Bubble>
    </BubbleRow>
  );
}

const ms = (n: number | undefined) =>
  n === undefined
    ? null
    : n >= 1000
      ? `${(n / 1000).toFixed(2)} s`
      : `${Math.round(n)} ms`;

type StarterState = NonNullable<MessageMetrics["starter"]>;

/**
 * Why no prefix was spoken, in the two words that fit on the line.
 *
 * Every outcome used to be printed as "(chiffre/date)" — the only reason that existed when
 * the starter was a fast remote model. The local hook predictor fails in four other ways,
 * and a sidecar timeout labelled as a refused date sends you looking at the wrong filter.
 */
const starterNoteFor = (
  starter: StarterState,
): { label: string; title: string } | null => {
  switch (starter.rejected_reason) {
    case "numeric_or_temporal":
      return {
        label: "chiffre/date",
        title:
          "Amorce rejetée : elle affirmait une info chiffrée ou temporelle (date, heure…).",
      };
    case "deadline":
      return {
        label: "trop lent",
        title:
          "L'amorce n'est pas arrivée dans son budget : le tour est parti sans elle.",
      };
    case "unavailable":
      return {
        label: starter.reason
          ? `indisponible : ${starter.reason}`
          : "indisponible",
        title:
          "Aucune prédiction : sidecar injoignable, modèles encore en chargement, ou délai dépassé.",
      };
    case "class":
      return {
        label: starter.hook_class
          ? `classe ${starter.hook_class.toLowerCase()}`
          : "classe non parlée",
        title:
          "Le prédicteur a choisi de ne rien dire (SILENCE) ou de relancer le client (RELANCE) — une question ne peut pas servir d'amorce.",
      };
    case "greeting":
      return {
        label: "salutation tardive",
        title:
          "Le prédicteur voulait dire bonjour alors que l'appel est déjà engagé — l'agent a salué au premier tour, il ne recommence pas.",
      };
    case "repeat":
      return {
        label: "déjà dite",
        title:
          "Toutes les amorces de la classe ont été dites il y a moins de 4 tours : l'agent ne répète pas la même amorce si tôt.",
      };
    case "language":
      return {
        label: "langue non couverte",
        title:
          "Le prédicteur ne parle que français ; l'appel est dans une autre langue.",
      };
    default:
      return null;
  }
};

function MessageMetricsDebug({ metrics }: { metrics?: MessageMetrics }) {
  const llm = metrics?.llm;
  const latency = metrics?.latency;
  const starter = metrics?.starter;
  const starterNote = starter ? starterNoteFor(starter) : null;
  const byModel = llm?.by_model ? Object.values(llm.by_model) : [];
  if (!llm && !latency && !starter) return null;

  return (
    <div className="mt-2 pt-2 border-t border-current/10 space-y-1 text-[10px] opacity-70">
      {byModel.map((m) => {
        const isStarter = m.provider === "bedrock";
        return (
          <div
            key={`${m.provider}:${m.model}`}
            className="flex items-center gap-1 flex-wrap"
          >
            <span className="font-medium">{m.provider}</span>
            <span>· {m.model}</span>
            {isStarter && (
              <span className="px-1 rounded bg-current/10 uppercase tracking-wide">
                starter
              </span>
            )}
            {m.ttft_ms !== undefined && (
              <span title="TTFT pur du provider">· {ms(m.ttft_ms)}</span>
            )}
            <span title="Tokens (total)">· {m.total_tokens} tok</span>
          </div>
        );
      })}
      <div className="flex items-center gap-2 flex-wrap">
        {latency?.response_delay_ms !== undefined && (
          <span title="Délai jusqu'au 1er chunk audio">
            délai {ms(latency.response_delay_ms)}
          </span>
        )}
        {metrics?.fallback && metrics.fallback !== "not_needed" && (
          <span className="px-1 rounded bg-current/10">
            fallback:{" "}
            {metrics.fallback === "called_used"
              ? "utilisé"
              : metrics.fallback === "called_failed"
                ? "en échec"
                : "appelé"}
          </span>
        )}
      </div>
      {starter && (starter.attempted || starter.rejected) && (
        <div className="flex items-center gap-1 flex-wrap">
          <span
            className={`px-1 rounded uppercase tracking-wide ${
              starter.rejected ? "bg-warning/20" : "bg-current/10"
            }`}
          >
            starter{starter.source === "predictor" ? " (predictor)" : ""}{" "}
            {starter.rejected ? "refusé" : starter.used ? "utilisé" : "vide"}
          </span>
          {starterNote && (
            <span title={starterNote.title}>({starterNote.label})</span>
          )}
          {starter.prefix && (
            <span
              className="italic opacity-80"
              title="Préfixe produit par le starter"
            >
              « {starter.prefix} »
            </span>
          )}
          {ms(starter.ms) && <span>· {ms(starter.ms)}</span>}
        </div>
      )}
    </div>
  );
}
